import React, { useState, useEffect, useContext } from 'react';
import RichText from '../Global/RichText';
import { ProductDescriptionProps } from './index';
import {
    getMultiChoice,
    getMediaData,
    getColor,
} from '../Global/DataUtils/index';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import Button from '../Global/Button';
//@ts-ignore
import Truncate from 'react-truncate';
import { getImageData } from '../Global/DataUtils/gatsbyImageUtils';
//@ts-ignore
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import { PageMetadataContext } from '../../templates/PageMetaDataContext';
import { getLangPrefix } from '../../global/countries';
//@ts-ignore
import * as styles from './product-description.module.css';
import {
    optimizeKontentImageUrl,
    buildKontentSrcSet,
} from '../Global/DataUtils';
import LazyImage from '../Global/LazyImage';

import Jotform from '../Jotform';

const ProductDescription: React.FC<ProductDescriptionProps> = ({
    cta_function,
    description_image,
    long_description,
    contact_form,
    product_display_name,
    content_panel__content_panel_font_color,
    content_panel__content_panel_color,
    secondary_image,
    kontentItemId,
    ...props
}) => {
    const [showFullDescription, setShowFullDescription] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);

    const descriptionImage = getImageData(description_image);
    const ghostedIcon = getMediaData(secondary_image);
    const cta = {
        label: props.cta_configuration__cta_label?.value,
        href: BuildLinkHref(
            props.cta_configuration__cta_location,
            props.cta_configuration__cta_location_anchor_point
        ),
        action: getMultiChoice(props.cta_configuration__cta_window_action),
    };
    const fontColor = getColor(content_panel__content_panel_font_color);
    const panelColor = getColor(content_panel__content_panel_color);
    const authorStyles =
        fontColor || panelColor || ghostedIcon?.url
            ? {
                  color: fontColor || undefined,
                  backgroundColor: panelColor || undefined,
                  backgroundImage: ghostedIcon?.url
                      ? `url(${ghostedIcon?.url})`
                      : undefined,
                  backgroundPosition: ghostedIcon?.url
                      ? 'center right'
                      : undefined,
              }
            : null;

    useEffect(() => {
        setShowFullDescription(false);
    }, []);

    const breakpoints = useBreakpoint();

    const readMoreAction = () => {
        return (
            <span className={styles.gradientOverlay}>
                <button
                    className={styles.readMoreButton}
                    onClick={() => setShowFullDescription(true)}
                >
                    Read More
                </button>
            </span>
        );
    };

    const renderCta = () => {
        const ctaFunction = getMultiChoice(cta_function);
        if (ctaFunction === 'form') {
            return (
                <Button
                    onClick={() => setShowForm(true)}
                    visualStyle="outlineLight"
                    type="button"
                >
                    {cta.label}
                </Button>
            );
        } else if (ctaFunction === 'page_link') {
            const pageMetadata = useContext(PageMetadataContext);

            const preferredLanguage =
                pageMetadata.preferredLanguage || 'default';
            const linkHref = `${getLangPrefix(preferredLanguage)}${cta.href}`;

            return (
                <a href={linkHref} className={styles.ctaLink}>
                    {cta.label}
                </a>
            );
        } else {
            // no_cta
            return null;
        }
    };

    return (
        <div className={styles.wrapper} data-kontent-item-id={kontentItemId}>
            <div
                className={styles.imageContainer}
                data-kontent-element-codename="description_image"
            >
                {descriptionImage?.image && (
                    <LazyImage
                        src={
                            descriptionImage.image
                                ? optimizeKontentImageUrl(
                                      (descriptionImage.image?.url ||
                                          descriptionImage.image) as string,
                                      {
                                          w: 800,
                                          fit: 'crop',
                                          q: 80,
                                          auto: 'format',
                                      }
                                  )
                                : ''
                        }
                        srcSet={
                            descriptionImage.image
                                ? buildKontentSrcSet(
                                      (descriptionImage.image?.url ||
                                          descriptionImage.image) as string,
                                      800,
                                      { fit: 'crop', q: 80, auto: 'format' }
                                  )
                                : ''
                        }
                        sizes="(max-width: 800px) 100vw, 800px"
                        alt={descriptionImage.alt}
                        width={800}
                        height={
                            descriptionImage.image.height
                                ? Math.round(
                                      (800 / descriptionImage.image.width) *
                                          descriptionImage.image.height
                                  )
                                : 600
                        }
                        className={styles.image}
                        style={{
                            width: '100%',
                            height: 'auto',
                            objectFit: 'cover',
                        }}
                    />
                )}
            </div>
            <div className={styles.contentContainer} style={authorStyles}>
                <div className={styles.innerContent}>
                    <div
                        className={styles.productName}
                        data-kontent-element-codename="product_display_name"
                    >
                        {product_display_name.value}
                    </div>
                    <div>
                        {breakpoints.xl || showFullDescription ? (
                            RichText(
                                long_description,
                                false,
                                undefined,
                                undefined,
                                'long_description'
                            )
                        ) : (
                            <Truncate
                                lines={12}
                                ellipsis={readMoreAction()}
                                onTruncate={(truncate) =>
                                    setIsTruncated(truncate)
                                }
                                trimWhitespace
                            >
                                {RichText(
                                    long_description,
                                    false,
                                    undefined,
                                    undefined,
                                    'long_description'
                                )}
                            </Truncate>
                        )}
                    </div>
                    {breakpoints.xl || showFullDescription || !isTruncated
                        ? renderCta()
                        : null}
                </div>
                {showForm && (
                    <div className={styles.typeform}>
                        <Jotform
                            formUrl={contact_form?.value}
                            formStyle="popup"
                            display={true}
                            onPopUpClose={() => setShowForm(false)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProductDescription;
